<script setup lang="ts">
import type { ContentBlockOwner } from '../../../model/assessments/store/interfaces'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useContentBlockList } from '../../../model/assessments/content-block-list.hook'
import ContentBlockText from './ContentBlockText.vue'
import ContentBlockImage from './ContentBlockImage.vue'
import ContentBlockAudio from './ContentBlockAudio.vue'

defineOptions({
  name: 'ContentBlockList',
})

const props = defineProps<{
  /** `{ screenId }` edits the screen's own content; `{ screenId, activityId }` an activity's. */
  owner: ContentBlockOwner
  label?: string
}>()

const {
  refBlocks,
  refHasBlocks,
  addBlock,
  removeBlock,
  moveBlockUp,
  moveBlockDown,
} = useContentBlockList({ props })
</script>

<template>
  <div class="content-block-list">
    <AppText name="body2-medium">
      {{ label ?? 'Content' }}
    </AppText>

    <AppText
      v-if="!refHasBlocks"
      name="body2"
      color="slate-500"
    >
      No content blocks yet.
    </AppText>

    <ul class="content-block-list__items">
      <li
        v-for="item in refBlocks"
        :key="`${item.index}-${item.block.type}`"
        class="content-block-list__item"
      >
        <div class="content-block-list__head">
          <AppText
            name="body2-medium"
            color="slate-500"
          >
            {{ item.block.type }}
          </AppText>
          <div class="content-block-list__actions">
            <BtnIcon
              :icon="faArrowUp"
              size="small"
              title="Move block up"
              :disabled="item.isFirst"
              @click="moveBlockUp(item.index)"
            />
            <BtnIcon
              :icon="faArrowDown"
              size="small"
              title="Move block down"
              :disabled="item.isLast"
              @click="moveBlockDown(item.index)"
            />
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              title="Remove block"
              @click="removeBlock(item.index)"
            />
          </div>
        </div>

        <ContentBlockText
          v-if="item.block.type === 'text'"
          :owner="owner"
          :index="item.index"
          :block="item.block"
        />
        <ContentBlockImage
          v-else-if="item.block.type === 'image'"
          :owner="owner"
          :index="item.index"
          :block="item.block"
        />
        <ContentBlockAudio
          v-else
          :owner="owner"
          :index="item.index"
          :block="item.block"
        />
      </li>
    </ul>

    <div class="content-block-list__add">
      <BtnSecondary
        text="Add text"
        @click="addBlock('text')"
      />
      <BtnSecondary
        text="Add image"
        @click="addBlock('image')"
      />
      <BtnSecondary
        text="Add audio"
        @click="addBlock('audio')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./ContentBlockList.scss" />
